import { moment } from "../../../ExternalHacks";

import {
  emailAddressFormLogic,
  GenericForm,
  GenericFormLogic,
  GenericFormValidationContext,
  genericBeforeValidate_removeDefaultKeyedError,
  genericValidate_textField,
  nameFormLogic,
  ValidationEvent,
} from "../../../Common";

import { ReactGiftCardInitialStateInterface } from "..";

export type GiftcardAmount =
  ReactGiftCardInitialStateInterface["amounts"][number];

export interface PurchaseForm extends GenericForm {
  giftcardAmount?: GiftcardAmount;
  giftcardAmountError?: string;

  to?: string;
  toError?: string;

  from?: string;
  fromError?: string;

  emailAddress?: string;
  emailAddressError?: string;

  deliveryDate: string;
  deliveryDateError?: string;

  personalMessage?: string;
  personalMessageNotice: string;
  personalMessageNoticeWarningColor?: boolean;
  personalMessageError?: string;
}

const dateToForm = (value: any) => moment(value).toISOString();

const maxPersonalMessageLength = 255;

const personalMessageNoticeFactory = (
  personalMessage: string | null | undefined
) =>
  `Maximum ${maxPersonalMessageLength} characters (${
    maxPersonalMessageLength - (personalMessage || "").length
  } remaining).`;

const isValidPersonalMessage = (personalMessage: string | null | undefined) => {
  const validCharactersRegex = new RegExp("^[a-zA-Z0-9 _@!,.*?'\"()\\-:;&#\\n\\r]+$|^$");
  return validCharactersRegex.test(personalMessage || "");
};

export interface PurchaseFormApp {}

/*
Omit<
    PurchaseForm,
    | "giftcardAmountError"
    | "toError"
    | "fromError"
    | "emailAddressError"
    | "deliveryDateError"
    | "personalMessageNoticeWarningColor"
    | "personalMessageError"
    | "personalMessageNotice"
  >,
*/

export const PurchaseFormLogic: GenericFormLogic<
  PurchaseForm,
  PurchaseFormApp
> = {
  giftcardAmount: {
    validationEvents: [ValidationEvent.onChange, ValidationEvent.onSubmit],
    beforeValidate: genericBeforeValidate_removeDefaultKeyedError,
    validate: (context) => {
      if (!context.form?.giftcardAmount) {
        context.mutate.withPartialForm(context, {
          giftcardAmountError: "Please enter an e-gift card amount.",
        });
      }
    },
  },
  to: nameFormLogic({ required: true }),
  from: nameFormLogic({ required: true }),
  emailAddress: emailAddressFormLogic({ required: true }),
  deliveryDate: {
    validationEvents: [ValidationEvent.onChange, ValidationEvent.onSubmit],
    beforeValidate: genericBeforeValidate_removeDefaultKeyedError,
    validate: (context) => {
      if (context.form?.deliveryDate) {
        const prevDeliveryDate = moment(context.prevForm.deliveryDate);
        const deliveryDate = moment(context.form.deliveryDate);
        const currentValue = context.form.deliveryDate;
        const proposedValue = dateToForm(deliveryDate);
        if (
          !prevDeliveryDate.isSame(deliveryDate) ||
          proposedValue !== currentValue
        ) {
          context.mutate.withPartialForm(context, {
            deliveryDate: proposedValue,
          });
        }
      } else {
        context.mutate.withPartialForm(context, {
          deliveryDateError: "This field is required.",
        });
      }
    },
  },
  personalMessage: {
    validationEvents: [
      ValidationEvent.onChange,
      ValidationEvent.onBlur,
      ValidationEvent.onSubmit,
    ],
    beforeValidate: genericBeforeValidate_removeDefaultKeyedError,
    validate: (context) =>
      genericValidate_textField<PurchaseForm, PurchaseFormApp>({
        maxLength: maxPersonalMessageLength,
        customErrorValidation: (newPersonalMessage: any) => {
          if (!isValidPersonalMessage(newPersonalMessage)) {
            return "Please only use letters, numbers, and common punctuation for your message.";
          }
          return null;
        },
        noticeGenerator: (newPersonalMessage: any): Partial<PurchaseForm> => {
          const personalMessageNotice =
            personalMessageNoticeFactory(newPersonalMessage);

          const personalMessageNoticeWarningColor =
            maxPersonalMessageLength - (newPersonalMessage || "").length < 1;

          return {
            personalMessageNoticeWarningColor,
            personalMessageNotice,
          };
        },
      })(context),
  },
} as const;

export const initialPurchaseForm = (): PurchaseForm => ({
  deliveryDate: dateToForm(moment()),

  personalMessageNotice: personalMessageNoticeFactory(""),
});
